import { useState } from 'react'

import { useAppSelector } from '../../store/hooks'
import { selectDocumentProspectingCurrentUser } from '../../store/processDocument/documentProspecting'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { type IntegrationServiceId, getIntegrationDisplayName } from '../../common/integrations'
import { getConnectedDocumentServiceIds, sortDocumentServiceIdsByDisplayName } from '../../common/getConnectedDocumentServiceIds'
import { useLastUsedDocumentConnector } from '../../common/lastUsedDocumentConnector'
import { DocumentConnectorActionButton, type DocumentConnectorActionItem } from '../DocumentConnector/DocumentConnectorActionButton'
import { type FileBrowserItem } from './types'
import { DocumentFileBrowserModal } from './DocumentFileBrowserModal'

interface DocumentIntegrationBrowseButtonsProps {
  onFilesSelected: (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => void
}

export const DocumentIntegrationBrowseButtons = ({ onFilesSelected }: DocumentIntegrationBrowseButtonsProps) => {
  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)
  const [activeServiceId, setActiveServiceId] = useState<IntegrationServiceId | null>(null)
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()
  const { trackingClient } = useTracking()

  const connectedDocumentServiceIds = getConnectedDocumentServiceIds(currentUser)

  if (connectedDocumentServiceIds.length === 0) {
    return null
  }

  const items: DocumentConnectorActionItem[] = sortDocumentServiceIdsByDisplayName(connectedDocumentServiceIds).map((serviceId) => ({
    key: serviceId,
    serviceId,
    label: `Browse in ${getIntegrationDisplayName(serviceId)}`,
  }))

  return (
    <>
      <DocumentConnectorActionButton
        items={items}
        primaryKey={lastUsedServiceId ?? undefined}
        onSelect={(key) => {
          const serviceId = key as IntegrationServiceId
          recordLastUsedServiceId(serviceId)
          setActiveServiceId(serviceId)
        }}
      />
      {activeServiceId ? (
        <DocumentFileBrowserModal
          show
          onHide={() => setActiveServiceId(null)}
          serviceId={activeServiceId}
          selectionMode='single-file'
          onFilesSelected={(items) => {
            trackingClient.track({ eventName: TrackEvent.DOCUMENT_UPLOADED_FROM_EXTERNAL_SOURCE, eventDetails: { system: activeServiceId } })
            onFilesSelected(activeServiceId, items)
          }}
        />
      ) : null}
    </>
  )
}
